/**
 * What each canvas of a live tree holds, from one walk: the light lists of every SubViewport, and
 * the CanvasModulate of the canvas the walk starts in. Godot keeps lights per viewport and
 * CanvasModulate per canvas, so one walk answers both with one rule each.
 */
import { MAX_2D_DIRECTIONAL_LIGHTS } from '../godot/rendering/rendering.js';
import { isViewportBoundary } from '../nodes/viewport/subviewport/viewportBoundary.js';
import { perObject } from '../utils/perObject.js';
import { isCanvasLayerType } from './canvasPaintOrder.js';
/**
 * Canvas lights that take a slot in the positional list. A `DirectionalLight2D` sits on a list of
 * its own (`renderer_viewport.cpp:491-514`), applied ahead of every positional light.
 */
const POSITIONAL_LIGHT_TYPES = new Set(['PointLight2D']);
/** True for a node that occupies a slot in the positional canvas light list. */
function isPositionalCanvasLight(node) {
    return POSITIONAL_LIGHT_TYPES.has(node.type);
}
/** Whether a node is shown, reading the parsed `visible` a CanvasItem parser leaves unset by default. */
function isShownCanvasNode(node) {
    return node.properties.visible !== false;
}
/**
 * True for a DirectionalLight2D that reaches the directional list. `Light2D::_update_light_visibility`
 * (`light_2d.cpp:59`) enables the light only while it is `enabled` and visible in the tree, and the
 * viewport lists only enabled lights, so a disabled or hidden light takes no slot.
 */
function isListedDirectionalLight(node) {
    return (node.type === 'DirectionalLight2D' &&
        node.properties.enabled !== false &&
        isShownCanvasNode(node));
}
function newCanvasLights() {
    return { positional: [], directional: [] };
}
/** Opens a canvas for the next CanvasLayer in `canvases`, and returns its index. */
function attachCanvas(canvases) {
    return canvases.push(newCanvasLights()) - 1;
}
const OWN_CANVAS = { viewportPath: null, isHidden: false, canvas: 0 };
/**
 * The directional lights a viewport lists, canvas by canvas: its own, then each CanvasLayer in tree
 * order (`renderer_viewport.cpp:441`). Godot stops at the eighth light with a `break` that leaves
 * only the loop of its canvas (`renderer_viewport.cpp:511`), so a later canvas lists past the cap.
 */
function gatherDirectional(canvases) {
    const listed = [];
    for (const { directional } of canvases) {
        for (const path of directional) {
            listed.push(path);
            if (listed.length === MAX_2D_DIRECTIONAL_LIGHTS)
                break;
        }
    }
    return listed;
}
function lightPathsOf(canvases) {
    return {
        positional: canvases.flatMap((canvas) => canvas.positional),
        directional: gatherDirectional(canvases),
    };
}
function scopesOfCanvases(tree) {
    const canvasesByViewport = new Map();
    const canvasesOf = (viewportPath) => {
        let canvases = canvasesByViewport.get(viewportPath);
        if (!canvases)
            canvasesByViewport.set(viewportPath, (canvases = [newCanvasLights()]));
        return canvases;
    };
    let modulate = null;
    tree.fold(OWN_CANVAS, ({ step, path }, scope, descend) => {
        const { node } = step;
        const canvases = canvasesOf(scope.viewportPath);
        const lights = canvases[scope.canvas];
        if (isPositionalCanvasLight(node))
            lights.positional.push(path);
        if (isListedDirectionalLight(node) && !scope.isHidden)
            lights.directional.push(path);
        const { color } = node.properties;
        if (node.type === 'CanvasModulate' &&
            color &&
            scope.viewportPath === null &&
            scope.canvas === 0 &&
            !scope.isHidden &&
            isShownCanvasNode(node)) {
            modulate = color;
        }
        if (isViewportBoundary(node.type)) {
            descend({ viewportPath: path, isHidden: false, canvas: 0 });
            return;
        }
        const inherited = isShownCanvasNode(node) ? scope : { ...scope, isHidden: true };
        descend(isCanvasLayerType(node.type) ? { ...inherited, canvas: attachCanvas(canvases) } : inherited);
    });
    const lights = new Map();
    for (const [viewportPath, canvases] of canvasesByViewport)
        lights.set(viewportPath, lightPathsOf(canvases));
    return { lights, modulate };
}
/**
 * The scopes of `tree`, in preorder. Every reader of one tree shares the walk, so a light's slot and
 * a canvas's tint cost one pass between them.
 *
 * @canonical What each canvas of a live tree holds: its light lists and its CanvasModulate
 */
export const canvasScopesOf = perObject(scopesOfCanvases);
//# sourceMappingURL=canvasScopes.js.map